import { Col } from '@rematter/ui-web';
import { LucideBellOff, LucideLoader2 } from 'lucide-react';

import { NotificationItem } from './NotificationItem';

import { useTranslations } from '~/modules/notifications/i18n/useTranslations';
import { NotificationFeedEntry } from '~/modules/notifications/types';

interface NotificationListProps {
  entries: NotificationFeedEntry[];
  isLoading: boolean;
  hasError: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onMarkAsRead: (id: string) => void;
  onNavigate: (actionLink: string) => void;
}

export const NotificationList = ({
  entries,
  isLoading,
  hasError,
  hasMore,
  onLoadMore,
  onMarkAsRead,
  onNavigate,
}: NotificationListProps) => {
  const t = useTranslations();

  if (hasError) {
    return (
      <Col xCenter yCenter className="py-12 text-neutral/50">
        <LucideBellOff className="mb-2 size-8" />
        <span className="text-sm font-medium">
          {t.notifications.errorTitle}
        </span>
        <span className="text-xs">{t.notifications.errorDescription}</span>
      </Col>
    );
  }

  if (!isLoading && entries.length === 0) {
    return (
      <Col xCenter yCenter className="py-12 text-neutral/50">
        <LucideBellOff className="mb-2 size-8" />
        <span className="text-sm font-medium">{t.notifications.empty}</span>
        <span className="text-xs">{t.notifications.emptyDescription}</span>
      </Col>
    );
  }

  return (
    <Col className="@NotificationList max-h-[400px] overflow-y-auto">
      {entries.map((entry) => (
        <NotificationItem
          key={entry.id}
          entry={entry}
          onMarkAsRead={onMarkAsRead}
          onNavigate={onNavigate}
        />
      ))}
      {isLoading && (
        <Col xCenter yCenter className="py-4">
          <LucideLoader2 className="size-5 animate-spin text-neutral/40" />
        </Col>
      )}
      {hasMore && !isLoading && (
        <button
          onClick={onLoadMore}
          className="w-full py-3 text-center text-xs text-primary hover:bg-neutral/5"
        >
          {t.notifications.viewAll}
        </button>
      )}
    </Col>
  );
};
